// WeekPlanner — the planner shell: view switcher (Day/Week/List — no month,
// owner decision), type filter chips, week navigation and the active view.
// Presentational: the page owns the cursor and the data, this component owns
// pure UI state (view mode, active filters, per-column expanders).

import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from '@govard/i18n-core/react';
import { Button, Card, Segmented, Spin } from 'antd';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Columns3,
  Flag,
  List,
  Phone,
  Send,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState, type ReactElement } from 'react';

import { useTheme } from '../../../theme/ThemeContext.js';
import {
  formatDayTitle,
  formatWeekRangeTitle,
  startOfWeekMonday,
  EVENT_TYPE_SURFACE,
  WIRED_EVENT_TYPES,
  type PlannerEvent,
  type PlannerEventType,
  type PlannerTypeSurface,
} from '../lib/plannerEvents.js';
import { PlannerDayView } from './PlannerDayView.js';
import { PlannerListView } from './PlannerListView.js';
import { PlannerWeekView, type QuickCreateRequest } from './PlannerWeekView.js';

export type PlannerViewMode = 'day' | 'week' | 'list';

/** True when the event target is a text-entry control that owns its keys. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable ||
    target.closest('.ant-modal-wrap, .ant-select-dropdown, .ant-popover') !== null
  );
}

/** Legend glyph per filter type — 10px, inherits the chip's family color. */
function LegendGlyph({ type }: { type: PlannerEventType }): ReactElement {
  if (type === 'tg') return <Send size={10} strokeWidth={2.25} />;
  if (type === 'meeting') return <Users size={10} strokeWidth={2} />;
  if (type === 'deadline') return <Flag size={10} strokeWidth={2} />;
  if (type === 'call') return <Phone size={10} strokeWidth={2} />;
  return <ClipboardList size={10} strokeWidth={2.25} />;
}

export interface WeekPlannerProps {
  events: PlannerEvent[];
  /** Anchor date: its week drives the grid, in day view the day itself. */
  cursor: Dayjs;
  onCursorChange: (next: Dayjs) => void;
  loading?: boolean | undefined;
  /** Event types that have a data source; chips render only for these. */
  availableTypes?: readonly PlannerEventType[] | undefined;
  /** Task checkbox toggle; `done` is the requested next state. */
  onToggleTask?: ((taskId: string, done: boolean) => void) | undefined;
  /** Quick-create: opens the Task/Meeting modal preset to the clicked day. */
  onQuickCreate?: ((request: QuickCreateRequest) => void) | undefined;
}

export function WeekPlanner({
  events,
  cursor,
  onCursorChange,
  loading = false,
  availableTypes = WIRED_EVENT_TYPES,
  onToggleTask,
  onQuickCreate,
}: WeekPlannerProps): ReactElement {
  const { t, locale } = useTranslation();
  const { actualTheme } = useTheme();
  const navigate = useNavigate();
  const mode = actualTheme === 'dark' ? 'dark' : 'light';

  const [viewMode, setViewMode] = useState<PlannerViewMode>('week');
  // Store the DISABLED types: newly available sources stay visible by default
  // even when the role-based set arrives after the first render.
  const [disabledTypes, setDisabledTypes] = useState<ReadonlySet<PlannerEventType>>(
    () => new Set<PlannerEventType>(),
  );

  const chipTypes = useMemo(
    () => availableTypes.filter((type) => type !== 'call'),
    [availableTypes],
  );
  const visibleEvents = useMemo(
    () => events.filter((event) => !disabledTypes.has(event.eventType)),
    [events, disabledTypes],
  );
  // Live per-type counters for the legend chips (already loaded events —
  // the anchor week's data, no extra requests).
  const typeCounts = useMemo(() => {
    const counts = new Map<PlannerEventType, number>();
    for (const event of events) {
      counts.set(event.eventType, (counts.get(event.eventType) ?? 0) + 1);
    }
    return counts;
  }, [events]);

  const weekStart = startOfWeekMonday(cursor);
  const weekStartKey = weekStart.format('YYYY-MM-DD');
  const now = dayjs();

  const handleOpen = (event: PlannerEvent): void => {
    navigate(event.href);
  };

  const handleToggleTask = (event: PlannerEvent): void => {
    if (event.taskId) onToggleTask?.(event.taskId, !event.done);
  };

  const toggleType = (type: PlannerEventType): void => {
    setDisabledTypes((prev) => {
      const next = new Set(prev);
      if (prev.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const stepDays = viewMode === 'day' ? 1 : 7;
  const rangeTitle =
    viewMode === 'day'
      ? formatDayTitle(cursor, locale, now)
      : formatWeekRangeTitle(weekStart, locale);
  const atHome =
    viewMode === 'day'
      ? cursor.isSame(now, 'day')
      : weekStart.isSame(startOfWeekMonday(now), 'day');
  const periodWord =
    viewMode === 'day' ? t('crm.calendar.period.day') : t('crm.calendar.period.week');

  // Keyboard navigation: ←/→ step the period, T jumps to today. Layout
  // independent (KeyT works for any Cyrillic/Latin layout). Ignored while
  // typing, while modifiers are held and inside modals/popovers.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (e.key === 'ArrowLeft') {
        onCursorChange(cursor.subtract(stepDays, 'day'));
        return;
      }
      if (e.key === 'ArrowRight') {
        onCursorChange(cursor.add(stepDays, 'day'));
        return;
      }
      if (e.code === 'KeyT' && !e.shiftKey) {
        onCursorChange(dayjs());
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cursor, stepDays, onCursorChange]);

  return (
    <div className="flex flex-col gap-4">
      <Card size="small" styles={{ body: { padding: 12 } }}>
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Segmented<PlannerViewMode>
              value={viewMode}
              onChange={setViewMode}
              options={[
                {
                  value: 'day',
                  label: t('crm.calendar.view.day'),
                  icon: <Calendar size={13} />,
                },
                {
                  value: 'week',
                  label: t('crm.calendar.view.week'),
                  icon: <Columns3 size={13} />,
                },
                {
                  value: 'list',
                  label: t('crm.calendar.view.list'),
                  icon: <List size={13} />,
                },
              ]}
            />
            {chipTypes.length > 1 ? (
              <div className="ml-auto flex flex-wrap items-center gap-1.5">
                {chipTypes.map((type) => {
                  const enabled = !disabledTypes.has(type);
                  const surface: PlannerTypeSurface = EVENT_TYPE_SURFACE[type][mode];
                  const typeCount = typeCounts.get(type) ?? 0;
                  return (
                    <button
                      key={type}
                      type="button"
                      aria-pressed={enabled}
                      onClick={() => toggleType(type)}
                      className="inline-flex h-[22px] items-center gap-1.5 rounded-[4px] border px-2 text-[10px] font-semibold uppercase tracking-[0.06em] transition-[border-color,background-color,color] duration-[110ms]"
                      style={
                        enabled
                          ? {
                              color: surface.text,
                              backgroundColor: surface.bg,
                              borderColor: surface.border,
                            }
                          : {
                              color: 'var(--ant-color-text-tertiary)',
                              backgroundColor: 'transparent',
                              borderColor: 'var(--ant-color-border-secondary)',
                            }
                      }
                    >
                      <span className="flex shrink-0 items-center opacity-85">
                        <LegendGlyph type={type} />
                      </span>
                      {t(`crm.calendar.filter.${type}`)}
                      {typeCount > 0 ? (
                        <span
                          className="shrink-0 font-mono text-[9.5px] font-semibold tabular-nums"
                          style={{ color: enabled ? surface.text : 'inherit', opacity: 0.85 }}
                        >
                          {typeCount}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Button
                aria-label={t('crm.calendar.nav.prev')}
                title={t('crm.calendar.nav.prev_hint', { period: periodWord })}
                icon={<ChevronLeft size={14} />}
                onClick={() => onCursorChange(cursor.subtract(stepDays, 'day'))}
                style={{ width: 28, height: 28, padding: 0, borderRadius: 4 }}
              />
              <Button
                onClick={() => onCursorChange(dayjs())}
                title={t('crm.calendar.nav.today_hint')}
                style={{
                  height: 28,
                  padding: '0 10px',
                  borderRadius: 4,
                  fontWeight: 550,
                  color: atHome ? 'var(--ant-color-text-secondary)' : undefined,
                }}
              >
                {t('crm.calendar.nav.today')}
              </Button>
              <Button
                aria-label={t('crm.calendar.nav.next')}
                title={t('crm.calendar.nav.next_hint', { period: periodWord })}
                icon={<ChevronRight size={14} />}
                onClick={() => onCursorChange(cursor.add(stepDays, 'day'))}
                style={{ width: 28, height: 28, padding: 0, borderRadius: 4 }}
              />
            </div>
            <span
              className="ml-1.5 text-[13px] font-semibold"
              style={{ color: 'var(--ant-color-text)' }}
            >
              {rangeTitle}
            </span>
            <span className="flex-1" />
            {loading ? <Spin size="small" /> : null}
          </div>
        </div>
      </Card>

      {loading && visibleEvents.length === 0 ? (
        <div className="flex justify-center py-16">
          <Spin size="large" />
        </div>
      ) : viewMode === 'week' ? (
        <PlannerWeekView
          key={weekStartKey}
          events={visibleEvents}
          weekStart={weekStart}
          onOpen={handleOpen}
          onToggleTask={handleToggleTask}
          onQuickCreate={onQuickCreate}
        />
      ) : viewMode === 'day' ? (
        <PlannerDayView
          key={cursor.format('YYYY-MM-DD')}
          events={visibleEvents}
          date={cursor}
          onOpen={handleOpen}
          onToggleTask={handleToggleTask}
          onQuickCreate={onQuickCreate}
        />
      ) : (
        <PlannerListView
          key={weekStartKey}
          events={visibleEvents}
          weekStart={weekStart}
          onOpen={handleOpen}
          onToggleTask={handleToggleTask}
        />
      )}
    </div>
  );
}
